import React, { useState, useRef } from 'react';
import type { User, PathwayBlock, OjtSession, OjtSessionObjective, OjtMode } from '../../../types';
import { DataService } from '../../../services/firebase';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Circle, Camera, Video, Mic, PenLine, ExternalLink, BookOpen } from 'lucide-react';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getApp } from 'firebase/app';

/** Local progress shape aligned to OjtSessionObjective.evidence */
interface ObjectiveProgress {
  photos: string[];
  videos: string[];
  voiceRecordings: { url: string; transcript?: string }[];
  voiceTranscript?: string; // interim transcript before saving
  leadHandSignature?: string;
  isCompleted: boolean;
}

const emptyProgress = (): ObjectiveProgress => ({
  photos: [],
  videos: [],
  voiceRecordings: [],
  isCompleted: false,
});

/** Restore progress from existing session objective data */
const restoreProgress = (sessionObjectives: OjtSessionObjective[] | undefined): Record<string, ObjectiveProgress> => {
  const map: Record<string, ObjectiveProgress> = {};
  if (!sessionObjectives) return map;
  for (const so of sessionObjectives) {
    map[so.objectiveId] = {
      photos: so.evidence?.photos || [],
      videos: so.evidence?.videos || [],
      voiceRecordings: so.evidence?.voiceRecordings || [],
      leadHandSignature: so.evidence?.leadHandSignature,
      isCompleted: so.isCompleted,
    };
  }
  return map;
};

interface ObjectiveExecutionProps {
  session: OjtSession;
  block: PathwayBlock;
  user: User;
  pathwayBlocks?: PathwayBlock[];
  /** Active OJT mode — displayed as a coloured badge in the header. */
  currentMode?: OjtMode;
  onSessionUpdate: (session: OjtSession) => void;
  onDone: () => void;
}

export const ObjectiveExecution: React.FC<ObjectiveExecutionProps> = ({
  session,
  block,
  user: _user,
  pathwayBlocks,
  currentMode,
  onSessionUpdate,
  onDone,
}) => {
  const { t } = useTranslation();
  const [expandedObjective, setExpandedObjective] = useState<string | null>(null);
  const [objectiveProgress, setObjectiveProgress] = useState<Record<string, ObjectiveProgress>>(() =>
    restoreProgress(session.objectives)
  );
  const [uploading, setUploading] = useState<string | null>(null);
  const [uploadingVideo, setUploadingVideo] = useState<string | null>(null);
  const recognitionRef = useRef<any>(null);
  const [isRecording, setIsRecording] = useState<string | null>(null);
  const leadHandCanvasRefs = useRef<Record<string, HTMLCanvasElement | null>>({});
  const [leadHandSigning, setLeadHandSigning] = useState<string | null>(null);
  const leadHandLastPos = useRef<{ x: number; y: number } | null>(null);
  const [leadHandDrawing, setLeadHandDrawing] = useState(false);

  const config = block.ojtConfig;
  const objectives = config?.objectives || [];

  const toggleObjective = (id: string) => {
    setExpandedObjective(prev => prev === id ? null : id);
  };

  /** Build OjtSessionObjective[] from current progress state */
  const buildSessionObjectives = (progress: Record<string, ObjectiveProgress>): OjtSessionObjective[] => {
    return objectives.map(obj => {
      const p = progress[obj.id] || emptyProgress();
      return {
        objectiveId: obj.id,
        label: obj.label,
        isCompleted: p.isCompleted,
        evidence: {
          photos: p.photos,
          videos: p.videos,
          voiceRecordings: p.voiceRecordings,
          leadHandSignature: p.leadHandSignature,
        },
      };
    });
  };

  const markComplete = async (objectiveId: string) => {
    const next = { ...objectiveProgress, [objectiveId]: { ...(objectiveProgress[objectiveId] || emptyProgress()), isCompleted: true } };
    setObjectiveProgress(next);
    const updatedSession: OjtSession = {
      ...session,
      objectives: buildSessionObjectives(next),
    };
    await DataService.updateOjtSession(updatedSession);
    onSessionUpdate(updatedSession);
  };

  const handleFileCapture = async (objectiveId: string, type: 'photo' | 'video') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = type === 'photo' ? 'image/*' : 'video/*';
    input.capture = 'environment';
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;
      type === 'photo' ? setUploading(objectiveId) : setUploadingVideo(objectiveId);
      try {
        const storage = getStorage(getApp());
        const ext = type === 'photo' ? 'jpg' : 'mp4';
        const contentType = type === 'photo' ? 'image/jpeg' : 'video/mp4';
        const storageRef = ref(storage, `soloready/ojt/${session.id}/objectives/${objectiveId}/${Date.now()}.${ext}`);
        await uploadBytes(storageRef, file, { contentType });
        const url = await getDownloadURL(storageRef);
        const prev = objectiveProgress[objectiveId] || emptyProgress();
        const arrayKey = type === 'photo' ? 'photos' : 'videos';
        const next = { ...objectiveProgress, [objectiveId]: { ...prev, [arrayKey]: [...prev[arrayKey], url] } };
        setObjectiveProgress(next);
      } catch (err) {
        console.error(`${type} upload failed`, err);
      } finally {
        type === 'photo' ? setUploading(null) : setUploadingVideo(null);
      }
    };
    input.click();
  };

  const startVoiceRecording = (objectiveId: string) => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) { alert('Speech recognition not supported.'); return; }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (e: any) => {
      const transcript = Array.from(e.results).map((r: any) => r[0].transcript).join('');
      setObjectiveProgress(prev => ({ ...prev, [objectiveId]: { ...(prev[objectiveId] || emptyProgress()), voiceTranscript: transcript } }));
    };
    recognition.start();
    recognitionRef.current = recognition;
    setIsRecording(objectiveId);
  };

  const stopVoiceRecording = (objectiveId: string) => {
    recognitionRef.current?.stop();
    setIsRecording(null);
    // Save the finalized transcript as a voice recording entry
    const prog = objectiveProgress[objectiveId];
    if (prog?.voiceTranscript) {
      const next = {
        ...objectiveProgress,
        [objectiveId]: {
          ...prog,
          voiceRecordings: [...prog.voiceRecordings, { url: '', transcript: prog.voiceTranscript }],
          voiceTranscript: undefined,
        },
      };
      setObjectiveProgress(next);
    }
  };

  // --- Lead hand signature helpers ---
  const getCanvasPos = (e: React.MouseEvent | React.TouchEvent, canvas: HTMLCanvasElement) => {
    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: (e as React.MouseEvent).clientX - rect.left, y: (e as React.MouseEvent).clientY - rect.top };
  };

  const startLeadHandDraw = (e: React.MouseEvent | React.TouchEvent, objectiveId: string) => {
    e.preventDefault();
    const canvas = leadHandCanvasRefs.current[objectiveId];
    if (!canvas) return;
    setLeadHandDrawing(true);
    leadHandLastPos.current = getCanvasPos(e, canvas);
  };

  const drawLeadHand = (e: React.MouseEvent | React.TouchEvent, objectiveId: string) => {
    e.preventDefault();
    if (!leadHandDrawing) return;
    const canvas = leadHandCanvasRefs.current[objectiveId];
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx || !leadHandLastPos.current) return;
    const pos = getCanvasPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(leadHandLastPos.current.x, leadHandLastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#0c4a6e';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.stroke();
    leadHandLastPos.current = pos;
    setLeadHandSigning(objectiveId);
  };

  const endLeadHandDraw = () => { setLeadHandDrawing(false); leadHandLastPos.current = null; };

  const saveLeadHandSignature = (objectiveId: string) => {
    const canvas = leadHandCanvasRefs.current[objectiveId];
    if (!canvas) return;
    const signatureData = canvas.toDataURL('image/png');
    const prev = objectiveProgress[objectiveId] || emptyProgress();
    const next = { ...objectiveProgress, [objectiveId]: { ...prev, leadHandSignature: signatureData } };
    setObjectiveProgress(next);
    setLeadHandSigning(null);
  };

  const clearLeadHandSignature = (objectiveId: string) => {
    const canvas = leadHandCanvasRefs.current[objectiveId];
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    const prev = objectiveProgress[objectiveId] || emptyProgress();
    const next = { ...objectiveProgress, [objectiveId]: { ...prev, leadHandSignature: undefined } };
    setObjectiveProgress(next);
    setLeadHandSigning(null);
  };

  /** Look up associated training block names from pathway blocks */
  const getAssociatedTrainingBlocks = (blockIds: string[]): PathwayBlock[] => {
    if (!pathwayBlocks || blockIds.length === 0) return [];
    return pathwayBlocks.filter(b => blockIds.includes(b.id));
  };

  const allRequired = objectives.every(obj => {
    const prog = objectiveProgress[obj.id];
    const evConfig = obj.evidenceConfig;
    if (!prog?.isCompleted) return false;
    if (evConfig?.photosRequired > 0 && (prog.photos || []).length < evConfig.photosRequired) return false;
    if (evConfig?.videosRequired > 0 && (prog.videos || []).length < evConfig.videosRequired) return false;
    if (evConfig?.leadHandSignatureRequired && !prog.leadHandSignature) return false;
    return true;
  });

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-3">
      <div className="bg-white rounded-xl border border-aero-200 p-4 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <h2 className="text-base font-bold text-aero-900 truncate">{t('candidate.ojt.objectives.title')}</h2>
          {currentMode && (
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider flex-shrink-0"
              style={{ backgroundColor: `${currentMode.color}20`, color: currentMode.color }}
              title={`Working in ${currentMode.label} mode`}
            >
              {currentMode.label}
            </span>
          )}
        </div>
        <span className="text-sm text-aero-500 flex-shrink-0">
          {objectives.filter(o => objectiveProgress[o.id]?.isCompleted).length}/{objectives.length}
        </span>
      </div>

      {objectives.map((obj) => {
        const prog = objectiveProgress[obj.id] || emptyProgress();
        const isExpanded = expandedObjective === obj.id;
        const evConfig = obj.evidenceConfig;

        return (
          <div key={obj.id} className={`bg-white rounded-xl border-2 transition-all ${prog.isCompleted ? 'border-green-300' : 'border-aero-200'}`}>
            <button
              onClick={() => toggleObjective(obj.id)}
              className="w-full flex items-start gap-3 p-4 text-left"
            >
              {prog.isCompleted ? (
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              ) : (
                <Circle className="w-5 h-5 text-aero-300 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <p className="text-sm font-semibold text-aero-900">{obj.label}</p>
                {obj.description && <p className="text-xs text-aero-500 mt-0.5">{obj.description}</p>}
              </div>
            </button>

            {isExpanded && !prog.isCompleted && (
              <div className="px-4 pb-4 space-y-3 border-t border-aero-100 pt-3">
                {/* Photos */}
                {evConfig?.photosRequired > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-aero-600 mb-2">
                      {t('candidate.ojt.objectives.evidence.photos', { count: evConfig.photosRequired })}
                      <span className="ml-1 text-aero-400">({prog.photos.length}/{evConfig.photosRequired})</span>
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      {prog.photos.map((url, idx) => (
                        <img key={idx} src={url} alt="" className="w-16 h-16 rounded-lg object-cover border border-aero-200" />
                      ))}
                      {prog.photos.length < evConfig.photosRequired && (
                        <button
                          onClick={() => handleFileCapture(obj.id, 'photo')}
                          disabled={uploading === obj.id}
                          className="w-16 h-16 rounded-lg border-2 border-dashed border-aero-300 flex items-center justify-center hover:border-aero-500 transition-colors"
                        >
                          {uploading === obj.id ? (
                            <span className="w-4 h-4 border-2 border-aero-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Camera className="w-5 h-5 text-aero-400" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Videos */}
                {evConfig?.videosRequired > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-aero-600 mb-2">
                      Videos required
                      <span className="ml-1 text-aero-400">({prog.videos.length}/{evConfig.videosRequired})</span>
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      {prog.videos.map((url, idx) => (
                        <div key={idx} className="w-24 h-16 rounded-lg border border-aero-200 overflow-hidden bg-black flex items-center justify-center">
                          <video src={url} className="w-full h-full object-cover" />
                        </div>
                      ))}
                      {prog.videos.length < evConfig.videosRequired && (
                        <button
                          onClick={() => handleFileCapture(obj.id, 'video')}
                          disabled={uploadingVideo === obj.id}
                          className="w-24 h-16 rounded-lg border-2 border-dashed border-aero-300 flex items-center justify-center hover:border-aero-500 transition-colors"
                        >
                          {uploadingVideo === obj.id ? (
                            <span className="w-4 h-4 border-2 border-aero-400 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Video className="w-5 h-5 text-aero-400" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Voice recording */}
                {evConfig?.voiceRecordingEnabled && (
                  <div>
                    <p className="text-xs font-semibold text-aero-600 mb-2">{t('candidate.ojt.objectives.evidence.voice')}</p>
                    {prog.voiceRecordings.length > 0 && (
                      <div className="space-y-1 mb-2">
                        {prog.voiceRecordings.map((vr, idx) => (
                          <p key={idx} className="text-xs text-aero-600 bg-aero-50 rounded-lg p-2">{vr.transcript || 'Recording saved'}</p>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => isRecording === obj.id ? stopVoiceRecording(obj.id) : startVoiceRecording(obj.id)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isRecording === obj.id ? 'bg-red-500 text-white' : 'bg-aero-100 text-aero-700 hover:bg-aero-200'}`}
                    >
                      <Mic className="w-4 h-4" />
                      {isRecording === obj.id ? t('candidate.ojt.objectives.stopRecording') : t('candidate.ojt.objectives.record')}
                    </button>
                    {prog.voiceTranscript && (
                      <p className="text-xs text-aero-600 mt-2 bg-aero-50 rounded-lg p-2 italic">{prog.voiceTranscript}</p>
                    )}
                  </div>
                )}

                {/* Lead hand signature (mission-critical) */}
                {evConfig?.leadHandSignatureRequired && (
                  <div>
                    <p className="text-xs font-semibold text-aero-600 mb-2 flex items-center gap-1">
                      <PenLine className="w-3 h-3" />
                      Lead Hand Signature Required
                    </p>
                    {prog.leadHandSignature ? (
                      <div className="space-y-2">
                        <img src={prog.leadHandSignature} alt="Lead hand signature" className="h-20 border border-aero-200 rounded-lg bg-aero-50" />
                        <button onClick={() => clearLeadHandSignature(obj.id)} className="text-xs text-aero-400 hover:text-red-500 transition-colors">
                          Clear & Re-sign
                        </button>
                      </div>
                    ) : (
                      <div>
                        <canvas
                          ref={(el) => { leadHandCanvasRefs.current[obj.id] = el; }}
                          width={500}
                          height={120}
                          className="w-full h-28 border-2 border-dashed border-aero-200 rounded-xl bg-aero-50 touch-none cursor-crosshair"
                          onMouseDown={(e) => startLeadHandDraw(e, obj.id)}
                          onMouseMove={(e) => drawLeadHand(e, obj.id)}
                          onMouseUp={endLeadHandDraw}
                          onMouseLeave={endLeadHandDraw}
                          onTouchStart={(e) => startLeadHandDraw(e, obj.id)}
                          onTouchMove={(e) => drawLeadHand(e, obj.id)}
                          onTouchEnd={endLeadHandDraw}
                        />
                        {leadHandSigning === obj.id && (
                          <button onClick={() => saveLeadHandSignature(obj.id)} className="mt-2 px-3 py-1.5 bg-aero-600 text-white rounded-lg text-xs font-semibold">
                            Save Signature
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Links */}
                {evConfig?.links?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-aero-600 mb-2">{t('candidate.ojt.objectives.evidence.links')}</p>
                    {evConfig.links.map((link: any, idx: number) => (
                      <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 rounded-lg border border-aero-200 hover:bg-aero-50 transition-colors mb-1">
                        <ExternalLink className="w-3.5 h-3.5 text-aero-400 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-medium text-aero-800">{link.title}</p>
                          {link.description && <p className="text-xs text-aero-400">{link.description}</p>}
                        </div>
                      </a>
                    ))}
                  </div>
                )}

                {/* Associated Training */}
                {evConfig?.associatedTrainingBlockIds?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-aero-600 mb-2 flex items-center gap-1">
                      <BookOpen className="w-3 h-3" />
                      Associated Training
                    </p>
                    {getAssociatedTrainingBlocks(evConfig.associatedTrainingBlockIds).map((tb) => (
                      <div key={tb.id} className="flex items-center gap-2 p-2 rounded-lg border border-aero-200 bg-aero-50 mb-1">
                        <BookOpen className="w-3.5 h-3.5 text-aero-400 flex-shrink-0" />
                        <p className="text-xs font-medium text-aero-800">{tb.name}</p>
                      </div>
                    ))}
                    {getAssociatedTrainingBlocks(evConfig.associatedTrainingBlockIds).length === 0 && (
                      <p className="text-xs text-aero-400">Training block references not available</p>
                    )}
                  </div>
                )}

                <button
                  onClick={() => markComplete(obj.id)}
                  disabled={
                    (evConfig?.photosRequired > 0 && prog.photos.length < evConfig.photosRequired) ||
                    (evConfig?.videosRequired > 0 && prog.videos.length < evConfig.videosRequired) ||
                    (evConfig?.leadHandSignatureRequired && !prog.leadHandSignature)
                  }
                  className="w-full py-2.5 bg-green-500 hover:bg-green-600 disabled:bg-aero-200 disabled:text-aero-400 text-white rounded-xl font-semibold text-sm transition-colors"
                >
                  {t('candidate.ojt.objectives.complete')}
                </button>
              </div>
            )}
          </div>
        );
      })}

      <button
        onClick={onDone}
        disabled={!allRequired && objectives.length > 0}
        className="w-full py-3.5 bg-aero-600 hover:bg-aero-700 disabled:bg-aero-200 disabled:text-aero-400 text-white rounded-xl font-semibold text-sm transition-colors mt-4"
      >
        Continue to Sign-Off
      </button>
    </div>
  );
};
